import { dispatch } from '../store/state/state-manager';
import * as ContextUtils from '../utils/context-utils';
import * as RoutingManager from '../router/routing-manager';
import {
  initContainers,
  addContainerItem,
  addTransientItem,
  updateTransientData,
  removeTransientItem,
  updateContainerItem,
  activateContainerItem,
  removeContainerItem,
  resetContainers
} from '../store/state/actions/action-creators';
import * as actionType from '../store/state/actions/index';
import { ActionManagerInstance } from '../actions/manager';
import constants from '../constants';
import { C11nEnv } from '../interpreter/c11n-env';

import {
  getDataContextName,
  resetContainersPayload,
  getTargetFromContainerItemID,
  getTransientItems,
  hasContainerItems,
  isContainerInitialized
} from './container-utils';
import FlowContainer from './flow-container';
import type { ContainerInfo, TransientContainerInfo, TransientItemInfo } from './types';

/**
 * @description
 * Provides the basic APIs to manage the containers that are used in complex
 * components such as the ViewContainer and FlowContainer. These composite
 * components use regions on the page called "containers" to map different
 * aspects of the data that's displayed.
 */
class ContainerManager {
  private readonly c11nEnv: C11nEnv;

  /**
   * Constructor - <strong>Not for use outside of Core! Included for reference only.</strong>
   * @param contextObj The context object that will be used for this instance of the Container Manager
   */
  constructor(contextObj: C11nEnv) {
    this.c11nEnv = contextObj;
  }

  /**
   * Create a promise for the container manager action and return an object containing the promise
   * and the ActionManager actionMgrID associated with that promise
   * @param theActionType the action type for which we're creating a promise
   * @returns object with actionMgrID and promise keys: { actionMgrID: <theID>, promise: <thePromise> }
   * @function
   * @private
   */

  containerManagerPromise = (theActionType: string) => {
    const theActionMgrID = ActionManagerInstance.getNextActionMgrId();
    let resolveFn;
    let rejectFn;
    const thePromise = new Promise((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });

    if (resolveFn && rejectFn) {
      ActionManagerInstance.register(theActionMgrID, thePromise, resolveFn, rejectFn, theActionType);
    } else {
      throw new Error('resolveFn or rejectFn is not defined.');
    }

    return {
      actionMgrID: theActionMgrID,
      promise: thePromise
    };
  };

  /**
   *
   * Initializes the container(s) in a complex component.
   *
   * @param containerInfo
   * Typically, the JSON object contains the <em>type</em> of the container being initialized.
   * For example: "single" or "multiple"
   * <br><br>
   * { type: "single"} or {type: "multiple"}
   * @returns A promise associated with the action (and stored in ActionManager)
   * @example <caption>Example for initializeContainers()</caption>
   * const containerManager = getPConnect().getContainerManager();
   * containerManager.initializeContainers({
   *    type: "multiple"
   *  });
   */
  initializeContainers(containerInfo: ContainerInfo) {
    const theType = actionType.INIT_CONTAINERS;

    const pConn = this.c11nEnv;
    const defaultBaseContext = pConn.getContextName();
    const defaultAcName = pConn.getContainerName();

    const defaultPayload = {
      context: defaultBaseContext,
      name: defaultAcName,
      type: constants.CONTAINER_TYPE.SINGLE
    };

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise
    const { actionMgrID, promise } = this.containerManagerPromise(theType);

    // When merging with spread operator, rightmost wins. So put actionInfo last...
    const mergedPayload = { ...defaultPayload, ...containerInfo, actionMgrID };

    dispatch(initContainers(mergedPayload));
    // return the Promise associated with this action
    return promise;
  }

  /**
   *
   * Adds a container item to the container
   *
   * @param containerInfo
   * Typically, the JSON object contains information about any _semantic URL_ associated with
   * the _context_ of the container. Additional information can specify a specific, targeted
   * region in the container (via _acName_), how the information should be rendered in the
   * container, etc.
   * <br><br>
   * Examples include:
   * <br>
   * { semanticURL: "", context: "app", acName: "primary"}
   * <br>and<br>
   * { semanticURL: "", context: "app/primary_5", caseViewMode: perform }
   * @returns A promise associated with the action (and stored in ActionManager)
   * @example <caption>Example for addContainerItem()</caption>
   * const containerManager = getPConnect().getContainerManager();
   * containerManager.addContainerItem({
   *    context: "app/primary_1",
   *    semanticURL: "RequestApprovals/REQ-1",
   *    caseViewMode: "review"
   *  });
   */
  addContainerItem(containerInfo: ContainerInfo) {
    const theType = actionType.ADD_CONTAINER_ITEM;

    const pConn = this.c11nEnv;
    const metadata = pConn.getMetadata();
    const { children } = metadata || {};
    let metaRoot;
    if (Array.isArray(children) && children.length > 0) {
      // gets the 0th element of children array
      [metaRoot] = children;
    }
    const defaultBaseContext = pConn.getContextName();
    const containerName = pConn.getContainerName();

    // default values for target and context if they're not in the payload
    const defaultContext = ContextUtils.buildContextName(
      containerName,
      defaultBaseContext,
      containerInfo?.containerType === constants.CONTAINER_TYPE.SINGLE
    );
    const defaultTarget = RoutingManager.getResolvedTargetName(containerName, defaultBaseContext);

    const defaultPayload = {
      root: {
        ...metaRoot,
        config: {
          ...metaRoot?.config,
          context: metaRoot?.config?.context || pConn.getPageReference?.()
        }
      },
      containerItemID: defaultContext,
      context: defaultContext,
      target: defaultTarget,
      containerName
    };

    containerInfo = JSON.parse(JSON.stringify(containerInfo));
    if ([constants.WORKAREA].includes(containerName)) {
      delete containerInfo.data?.coreHeaders;
    }

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise
    const { actionMgrID, promise } = this.containerManagerPromise(theType);

    // When merging with spread operator, rightmost wins. So put actionInfo (in containerInfo)
    //  after the defaultPayload. And then add in the actionMgrID that's needed by ActionManager
    const mergedPayload = { ...defaultPayload, ...containerInfo, actionMgrID };

    dispatch(addContainerItem(mergedPayload));

    // return the Promise associated with this action
    return promise;
  }

  /**
   * Updates the information within an item in the container
   * @param containerInfo
   * Typically, the JSON object contains information about context, target, containerItemID and any _semantic URL_ associated with
   * the _context_ of the container. Additional information can specify a specific, targeted
   * region in the container (via _acName_), how the information should be rendered in the
   * container.
   * @returns A promise associated with the action (and stored in ActionManager)
   * @example <caption>Example for updateContainerItem()</caption>
   * const containerManager = getPConnect().getContainerManager();
   *   containerManager.updateContainerItem({
   *   semanticURL: "RequestApprovals/REQ-1",
   *   caseViewMode: "review",
   *   target:"app/primary_0/workarea",
   *   containerItemID:"app/primary_0/workarea_0",
   *   context:"app/primary_0"
   * });
   */
  updateContainerItem(containerInfo: ContainerInfo) {
    if (!containerInfo?.containerItemID) {
      return Promise.reject(new Error('updateContainerItem : containerItemID is required in containerInfo object'));
    }
    const theType = actionType.UPDATE_CONTAINER_ITEM;
    // get target and context using containerItemID
    const context = getDataContextName(containerInfo.containerItemID);
    const target = getTargetFromContainerItemID(containerInfo.containerItemID);

    const defaultPayload = {
      context,
      target
    };
    const { actionMgrID, promise } = this.containerManagerPromise(theType);
    const mergedPayload = { ...defaultPayload, ...containerInfo, actionMgrID };
    dispatch(updateContainerItem(mergedPayload));
    return promise;
  }

  /**
     * Activates the container item in a container
     * @param containerInfo
     * this is the JSON Object containing containerItemID, target
     * @returns A promise associated with the action
     * @example <caption>Example for activateContainerItem()</caption>
     * const containerManager = getPConnect().getContainerManager();
        containerManager.activateContainerItem({
            target: "app/primary",
            containerItemID: "app/primary_3"
      });
     */
  activateContainerItem(containerInfo: ContainerInfo) {
    if (!containerInfo?.containerItemID) {
      return Promise.reject(new Error('activateContainerItem : containerItemID is required in containerInfo object'));
    }
    const theType = actionType.ACTIVATE_CONTAINER_ITEM;
    // get target using containerItemID
    const target = getTargetFromContainerItemID(containerInfo.containerItemID);
    const defaultPayload = {
      target
    };
    const { actionMgrID, promise } = this.containerManagerPromise(theType);
    const mergedPayload = { ...defaultPayload, ...containerInfo, actionMgrID };
    dispatch(activateContainerItem(mergedPayload));
    return promise;
  }

  /**
     * Removes a container item from a container
     * @param containerInfo
     * this is the JSON Object containing containerItemID, target
     * @returns A promise associated with the action
     * @example <caption>Example for removeContainerItem()</caption>
     * const containerManager = getPConnect().getContainerManager();
        containerManager.removeContainerItem({
        target: "app/primary",
        containerItemID: "app/primary_3"
      });
     */

  removeContainerItem(containerInfo: ContainerInfo) {
    if (!containerInfo?.containerItemID) {
      return Promise.reject(new Error('removeContainerItem : containerItemID is required in containerInfo object'));
    }
    const theType = actionType.REMOVE_CONTAINER_ITEM;
    // get target and context using containerItemID
    const context = getDataContextName(containerInfo.containerItemID);
    const target = getTargetFromContainerItemID(containerInfo.containerItemID);

    const defaultPayload = {
      context,
      target
    };
    const { actionMgrID, promise } = this.containerManagerPromise(theType);
    const mergedPayload = { ...defaultPayload, ...containerInfo, actionMgrID };
    dispatch(removeContainerItem(mergedPayload));
    return promise;
  }

  /**
   *
   * Reset the container(s) to its initial state.
   *
   * @param containerInfo
   * Typically, the JSON object contains information about context, containerName, containeritems associated with the container.
   * @returns A promise associated with the action
   * @example <caption>Example for resetContainers()</caption>
   * const containerManager = getPConnect().getContainerManager();
   * containerManager.resetContainers({
   *    context:"app",
   *    name:"preview",
   *    containerItems: ["app/preview_1","app/preview_2"]
   *  });
   */
  resetContainers(containerInfo: ContainerInfo) {
    if (!containerInfo?.context || !containerInfo?.name) {
      return Promise.reject(new Error('resetContainers : context and name are required in containerInfo object'));
    }
    const theType = actionType.RESET_CONTAINERS;

    const defaultPayload = resetContainersPayload(containerInfo);

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise
    const { actionMgrID, promise } = this.containerManagerPromise(theType);

    // When merging with spread operator, rightmost wins. So put actionInfo last...
    const mergedPayload = { ...defaultPayload, ...containerInfo, actionMgrID };

    dispatch(resetContainers(mergedPayload));
    // return the Promise associated with this action
    return promise;
  }

  /**
   * Add a transient item to the current context
   * which can be used to hold the values which doesn't
   * get submitted with content. Example: creating search filters which should not be submitted
   * along with the other data to server.
   * @param containerInfo JSON object containing info about id and data to add
   * whereas id is the unique id for the transient item name and
   * data contains the properties and their values
   * @returns newly created transient item id
   * @example <caption>Example for addTransientItem()</caption>
   * const containerManager = getPConnect().getContainerManager();
   * containerManager.addTransientItem({
   *  id: 'uniqueIdentifier',
   *  data: {
   *        "Prop1": "valueA",
   *        "Prop2": "valueB"
   *  }
   * });
   */
  addTransientItem(containerInfo: ContainerInfo): string {
    const { id, data } = containerInfo;
    const contextName = this.c11nEnv.getContextName();
    const transientItemID = ContextUtils.getTransientContext(id, contextName);
    const existingItems = getTransientItems(contextName);
    if (transientItemID && existingItems.includes(transientItemID)) {
      return transientItemID;
    }

    dispatch(
      addTransientItem({
        transientItemID,
        contextName,
        target: getTargetFromContainerItemID(contextName),
        data
      })
    );

    return transientItemID;
  }

  /**
   * Update a transient item in the current context
   * which can be used to hold the values which doesn't
   * get submitted with content. Example: creating search filters which should not be submitted
   * along with the other data to server.
   * @param transientObject JSON object containing info about id and data to add
   * whereas id is the unique id for the transient item and
   * data contains the properties and their values.
   * @returns for successful update of transient item id
   * @example <caption>Example for updateTransientData()</caption>
   * const containerManager = getPConnect().getContainerManager();
   * containerManager.updateTransientData({
   *  id: 'uniqueIdentifier',
   *  data: {
   *        "Prop1": "valueA",
   *        "Prop2": "valueB"
   *  }
   * });
   */

  updateTransientData(transientObject: TransientContainerInfo) {
    const { transientItemID, data } = transientObject;
    const contextName = this.c11nEnv.getContextName();

    if (transientItemID) {
      const existingItems = getTransientItems(contextName);
      if (!existingItems.includes(transientItemID)) {
        return;
      }
    } else {
      return;
    }

    dispatch(
      updateTransientData({
        transientItemID,
        data
      })
    );
  }

  /**
   * Remove a transient item from the current context
   * @param transientItemInfo JSON object containing target, containerItemID and transientItemId
   * @returns on removing transient item
   * @example <caption>Example for removeTransientItem()</caption>
   * const containerManager = getPConnect().getContainerManager();
   * containerManager.removeTransientItem('uniqueIdentifier');
   */
  removeTransientItem(transientItemInfo: TransientItemInfo) {
    const contextName = this.c11nEnv.getContextName();
    const { target = this.c11nEnv.getTarget(), containerItemID, transientItemID } = transientItemInfo;

    if (transientItemID && containerItemID) {
      const existingItems = getTransientItems(contextName);
      if (!existingItems.includes(transientItemID)) {
        return;
      }
    } else {
      return;
    }

    dispatch(
      removeTransientItem({
        target,
        containerItemID,
        transientItemID
      })
    );
  }

  createContainerPConnect(
    flowContainerInfo: { accessedOrder: any; items: any },
    pageReference: string,
    containerName: string,
    isAssignmentView: boolean = false
  ) {
    FlowContainer.createContainerPConnect(flowContainerInfo, pageReference, containerName, isAssignmentView);
  }

  hasContainerItems() {
    const contextName = this.c11nEnv.getContextName();
    const containerName = this.c11nEnv.getContainerName();
    return hasContainerItems(`${contextName}/${containerName}`);
  }

  isContainerInitialized() {
    const context = this.c11nEnv.getContextName();
    const containerName = this.c11nEnv.getContainerName();
    return isContainerInitialized(context, containerName);
  }
}
export default ContainerManager;
